import React, { type FC } from "react";

import {
  Box,
  type BoxProps,
  Column,
  DragIcon,
  useMergeRefs,
} from "@hightouch/htui";

import { type Condition, ConditionType } from "src/types/visual";

import { colors } from "./colors";
import { useDraggable } from "src/components/explore/draggable";

type Props = {
  conditionType: ConditionType.And | ConditionType.Or;
  condition?: Condition;
  index?: number;
  disableDrag?: boolean;
  forceDragStyles?: boolean;
  getContainerHeight?: () => number | undefined;
};

export const GroupIndicatorBar: FC<Readonly<Props & BoxProps>> =
  React.forwardRef(function GroupIndicatorBarNoRef(
    {
      conditionType = ConditionType.And,
      index = -1,
      condition,
      disableDrag,
      forceDragStyles,
      getContainerHeight,
      ...props
    },
    ref,
  ) {
    const { ref: dragRef, isDraggable } = useDraggable({
      index,
      id: condition?.id ?? "no-id",
      item: condition,
      disable: disableDrag,
      getContainerHeight,
    });

    const combinedRefs = useMergeRefs(ref, dragRef);
    return (
      <>
        <Column
          ref={combinedRefs}
          bgColor={
            forceDragStyles
              ? colors.hover[conditionType]
              : colors.base[conditionType]
          }
          borderTopLeftRadius="xl"
          borderBottomLeftRadius="xl"
          boxShadow="buttonShadowLight"
          flexShrink={0}
          width="24px"
          fontSize="24px"
          justifyContent="center"
          cursor={isDraggable ? "grab" : undefined}
          transition="background-color 200ms ease"
          _hover={{
            ".dragIcon": { opacity: 1 },
            bgColor: isDraggable
              ? colors.hover[conditionType]
              : colors.base[conditionType],
          }}
          {...props}
        >
          {(isDraggable || forceDragStyles) && (
            <Box
              opacity={forceDragStyles ? 1 : 0}
              transition="opacity 200ms ease"
              className="dragIcon"
            >
              <DragIcon color={colors.textHover[conditionType]} />
            </Box>
          )}
        </Column>
      </>
    );
  });
